import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AdminLayout } from '@/layouts/AdminLayout';
import { useClientStore } from '@/stores/client';
import { DashboardPage } from '@/pages/DashboardPage';
import { ResearchPage } from '@/pages/ResearchPage';
import { GraphPage } from '@/pages/GraphPage';
import { InsightsPage } from '@/pages/InsightsPage';
import { AgentsPage } from '@/pages/AgentsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { McpConnectorsPage } from '@/pages/McpConnectorsPage';
import { GenerationPage } from '@/pages/GenerationPage';
import { AdGenerationPage } from '@/pages/AdGenerationPage';
import { PersonasPage } from '@/pages/PersonasPage';
import { PersonaDetailPage } from '@/pages/PersonaDetailPage';
import { ScenariosPage } from '@/pages/ScenariosPage';
import { AssetPipelinePage } from '@/pages/AssetPipelinePage';
import { AdCatalogPage } from '@/pages/AdCatalogPage';
import { BrandDesignPage } from '@/pages/BrandDesignPage';
import { CreativeSkillsPage } from '@/pages/CreativeSkillsPage';
import { FrameworkLibraryPage } from '@/pages/FrameworkLibraryPage';
import { FrameworkDetailPage } from '@/pages/FrameworkDetailPage';
import { CreativeTemplatesPage } from '@/pages/CreativeTemplatesPage';
import { BriefTemplatesPage } from '@/pages/BriefTemplatesPage';
import { StudioPage } from '@/pages/StudioPage';
import { SkillLibraryPage } from '@/pages/SkillLibraryPage';
import { SkillsCatalogPage } from '@/pages/SkillsCatalogPage';
import { ClientPlaybooksPage } from '@/pages/ClientPlaybooksPage';
import {
  PlaybookListPage,
  PlaybookDetailPage,
} from '@/pages/PlaybookExplorerPage';
import {
  CreativeMemoryOverviewPage,
  PersonaRoutingPage,
  DecisionsBrowserPage,
  EscalationsPage,
} from '@/pages/CreativeMemoryPage';
import { UsageActivityPage } from '@/pages/UsageActivityPage';
import { PerformanceLoopPage } from '@/pages/PerformanceLoopPage';
import { AdAnalyticsPage } from '@/pages/AdAnalyticsPage';
import { KnowledgeManagementPage } from '@/pages/KnowledgeManagementPage';
import { RawConceptsPage } from '@/pages/RawConceptsPage';
import { ResearchRunsPage } from '@/pages/ResearchRunsPage';
import { LineagePage } from '@/pages/LineagePage';
import { CatalogIndexPage } from '@/pages/knowledge/catalogs/CatalogIndexPage';
import { OntologySchemaPage } from '@/pages/knowledge/catalogs/OntologySchemaPage';
import { ConceptTypePage } from '@/pages/knowledge/catalogs/ConceptTypePage';
import { AdTaxonomyCatalogPage } from '@/pages/knowledge/catalogs/AdTaxonomyCatalogPage';
import { CreativeStrategyIndexPage } from '@/pages/knowledge/creative-strategy/CreativeStrategyIndexPage';
import { DocketPage } from '@/pages/knowledge/creative-strategy/DocketPage';
import { CreativeStrategyPackPage } from '@/pages/knowledge/creative-strategy/CreativeStrategyPackPage';
import { CorpusPage } from '@/pages/knowledge/creative-strategy/CorpusPage';
import { StrategyTemplatesPage } from '@/pages/knowledge/creative-strategy/StrategyTemplatesPage';
import { StrategyRedirect } from '@/pages/knowledge/creative-strategy/StrategyRedirect';
import { ProposedCanonicalsPage } from '@/pages/knowledge/catalogs/ProposedCanonicalsPage';
import { FleetDashboardPage } from '@/pages/knowledge/fleet/FleetDashboardPage';
import { ClientFleetDetailPage } from '@/pages/knowledge/fleet/ClientFleetDetailPage';
import { ResolutionPipelinePage } from '@/pages/knowledge/resolution/ResolutionPipelinePage';
import { ReviewQueuePage } from '@/pages/knowledge/resolution/ReviewQueuePage';
import { QualityDashboardPage } from '@/pages/knowledge/quality/QualityDashboardPage';
import { AgentLabPage } from '@/pages/AgentLabPage';
import { ChatShellPage } from '@/pages/ChatShellPage';
import { CreativeWorkbenchLayout } from '@/pages/creative/CreativeWorkbenchLayout';
import { HomeView } from '@/pages/creative/HomeView';
import { StrategyView } from '@/pages/creative/StrategyView';
import { PerformanceView } from '@/pages/creative/PerformanceView';
import { SteeringInvestigationView } from '@/pages/creative/PerformanceView/SteeringInvestigationView';
import { ReviewedCoverageView } from '@/pages/creative/PerformanceView/ReviewedCoverageView';
import { FormulaAnalysisView } from '@/pages/creative/PerformanceView/FormulaAnalysisView';
import { CustomerQualityView } from '@/pages/creative/PerformanceView/CustomerQualityView';
import { CreativeFatigueView } from '@/pages/creative/PerformanceView/CreativeFatigueView';
import { CampaignPerformanceView } from '@/pages/creative/PerformanceView/CampaignPerformanceView';
import { FunnelAttributionView } from '@/pages/creative/PerformanceView/FunnelAttributionView';
import { PersonaDetailView as CreativePersonaDetailView } from '@/pages/creative/PerformanceView/PersonaDetailView';
import { BriefsView } from '@/pages/creative/BriefsView';
import { AssetsView } from '@/pages/creative/AssetsView';
import { CreativeWorkspacePage } from '@/pages/creative/CreativeWorkspace';
import { PackView } from '@/pages/creative/PackView';
import { LiveAssetViewerPage } from '@/pages/creative/LiveAssetViewerPage';
// Performance section (Phase 2)
import { PerformanceLayout } from '@/pages/performance/PerformanceLayout';
import { CampaignsAccountView } from '@/pages/performance/campaigns';
import { CampaignDetailView } from '@/pages/performance/campaigns/CampaignDetailView';
import { PersonaCohortView } from '@/pages/performance/personas';
import { PersonaDetailViewStandalone } from '@/pages/performance/personas/PersonaDetailViewStandalone';
import { CreativePerformanceLayout } from '@/pages/performance/creative';
import { VersionBrowser } from '@/pages/ontology/VersionBrowser';
import { VersionDiff } from '@/pages/ontology/VersionDiff';
import { ConceptInspector } from '@/pages/ontology/ConceptInspector';
import { ConceptDetail } from '@/pages/ontology/ConceptDetail';
import { BuildInspector } from '@/pages/ontology/BuildInspector';
import { OntologyExplorer } from '@/pages/ontology/OntologyExplorer';
import { GardenStudio } from '@/pages/garden/GardenStudio';
import { CompassComparison } from '@/pages/garden/CompassComparison';
import { CreativeReviewPage } from '@/pages/creative-review/CreativeReviewPage';
import { RunDetailView } from '@/pages/creative-review/RunDetailView';
import { GraphBuildDetailView } from '@/pages/creative-review/GraphBuildDetailView';

// Preserves the :canonicalId param while redirecting old deep links.
function KnowledgeDeepLinkRedirect() {
  const { canonicalId } = useParams<{ canonicalId: string }>();
  return (
    <Navigate
      to={`/knowledge/estate/entities/${canonicalId ?? ''}`}
      replace
    />
  );
}

// Redirects /briefs/:briefId → /creative/briefs/:briefId while preserving query.
function BriefsDetailRedirect() {
  const { briefId } = useParams<{ briefId: string }>();
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return (
    <Navigate
      to={`/creative/briefs/${briefId ?? ''}${search}`}
      replace
    />
  );
}

// Redirect any old /creative/performance/* deep link to /performance/creative/*.
function CreativePerformanceRedirect() {
  const params = useParams<{ '*'?: string }>();
  const rest = params['*'] ?? '';
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const dest = rest
    ? `/performance/creative/${rest}${search}`
    : `/performance/creative${search}`;
  return <Navigate to={dest} replace />;
}

// Redirect /admin/ontology and /admin/garden index routes to the user's
// currently selected client.
function OntologyClientFallback({ target }: { target: 'versions' | 'garden' }) {
  const clientUid = useClientStore((s) => s.selectedClientUid) ?? 'skoutorganic';
  const dest =
    target === 'garden'
      ? `/admin/garden/${clientUid}`
      : `/admin/ontology/${clientUid}/versions`;
  return <Navigate to={dest} replace />;
}

// Redirect /admin/creative-review to the selected client's runs page.
function CreativeReviewClientFallback() {
  const clientUid =
    useClientStore((s) => s.selectedClientUid) ?? 'skoutorganic';
  return (
    <Navigate to={`/admin/creative-review/${clientUid}/runs`} replace />
  );
}

export function AppRouter() {
  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="agent-lab" element={<AgentLabPage />} />
        {/* Skill-launched chat shell */}
        <Route path="chat/new" element={<ChatShellPage />} />
        <Route path="chat/:threadId" element={<ChatShellPage />} />
        <Route path="research" element={<ResearchPage />} />
        {/* Generation workflows - supports /generation, /generation/playbooks, /generation/:runId */}
        <Route path="generation" element={<GenerationPage />} />
        <Route path="generation/playbooks" element={<GenerationPage />} />
        <Route path="generation/:runId" element={<GenerationPage />} />
        {/* Creative Workbench — five-section shell (Phase 0 scaffold).
            The Performance sub-tree has moved to /performance/creative/* and
            this section now serves a backwards-compat redirect for the old
            URLs. */}
        <Route path="creative" element={<CreativeWorkbenchLayout />}>
          <Route index element={<HomeView />} />
          <Route path="strategy" element={<StrategyView />} />
          <Route path="performance/*" element={<CreativePerformanceRedirect />} />
          <Route path="briefs" element={<BriefsView />} />
          <Route path="briefs/:briefId" element={<BriefsView />} />
          <Route path="assets" element={<AssetsView />} />
          <Route path="assets/:assetId" element={<AssetsView />} />
          <Route path="assets/creative/:creativeId" element={<CreativeWorkspacePage />} />
          <Route path="packs/:packId" element={<PackView />} />
          {/* Live Asset Streaming — streaming-first viewer for in-flight
              packs. Replaces ``/ad-generation/:jobId`` as the post-launch
              landing surface. */}
          <Route path="live/:jobId" element={<LiveAssetViewerPage />} />
        </Route>

        {/* Performance — top-level reporting surface (Phase 2). */}
        <Route path="performance" element={<PerformanceLayout />}>
          <Route index element={<Navigate to="campaigns" replace />} />
          <Route path="campaigns">
            <Route index element={<CampaignsAccountView />} />
            <Route path=":campaignId" element={<CampaignDetailView />} />
          </Route>
          {/* Creative — port of the legacy /creative/performance/* pages,
              re-mounted under the new prefix. We mount each leaf component
              explicitly to avoid the legacy `endsWith('/performance/...')`
              dispatch in PerformanceView/index.tsx. */}
          <Route path="creative" element={<CreativePerformanceLayout />}>
            <Route index element={<PerformanceView />} />
            <Route path="persona/:personaId" element={<CreativePersonaDetailView />} />
            <Route path="steering" element={<SteeringInvestigationView />} />
            <Route path="coverage" element={<ReviewedCoverageView />} />
            <Route path="formulas" element={<FormulaAnalysisView />} />
            <Route path="quality" element={<CustomerQualityView />} />
            <Route path="fatigue" element={<CreativeFatigueView />} />
            <Route path="campaigns" element={<CampaignPerformanceView />} />
            <Route path="funnel" element={<FunnelAttributionView />} />
          </Route>
          <Route path="personas">
            <Route index element={<PersonaCohortView />} />
            <Route path=":personaId" element={<PersonaDetailViewStandalone />} />
          </Route>
        </Route>

        {/* Legacy briefs URLs — redirect to new Creative ▸ Briefs location */}
        <Route path="briefs" element={<Navigate to="/creative/briefs" replace />} />
        <Route path="briefs/:briefId" element={<BriefsDetailRedirect />} />
        {/* Ad generation - supports /ad-generation, /ad-generation/new, and /ad-generation/:jobId */}
        <Route path="ad-generation" element={<AdGenerationPage />} />
        <Route path="ad-generation/new" element={<AdGenerationPage />} />
        <Route path="ad-generation/:jobId" element={<AdGenerationPage />} />
        {/* Brand design - supports /brand-design, /brand-design/extract, /brand-design/:clientUid */}
        <Route path="brand-design" element={<BrandDesignPage />} />
        <Route path="brand-design/extract" element={<BrandDesignPage />} />
        <Route path="brand-design/:clientUid" element={<BrandDesignPage />} />
        <Route path="skills" element={<CreativeSkillsPage />} />
        <Route path="frameworks" element={<FrameworkLibraryPage />} />
        <Route path="frameworks/:id" element={<FrameworkDetailPage />} />
        <Route path="creative-templates" element={<CreativeTemplatesPage />} />
        <Route path="creative-templates/:templateId" element={<CreativeTemplatesPage />} />
        <Route path="brief-templates" element={<BriefTemplatesPage />} />
        <Route path="studio" element={<StudioPage />} />
        <Route path="studio/:clientUid" element={<StudioPage />} />
        <Route path="studio/:clientUid/:briefId" element={<StudioPage />} />
        <Route path="ad-catalog" element={<AdCatalogPage />} />
        <Route path="ad-catalog/:adId" element={<AdCatalogPage />} />
        <Route path="ad-analytics" element={<AdAnalyticsPage />} />
        <Route path="assets" element={<AssetPipelinePage />} />
        <Route path="graph" element={<GraphPage />} />
        {/* Legacy /ontology routes — redirect to the unified Explorer under /admin/ontology */}
        <Route path="ontology" element={<Navigate to="/admin/ontology" replace />} />
        <Route path="ontology/:version" element={<Navigate to="/admin/ontology" replace />} />

        {/* Decision Ontology Admin UI — Phase 5 */}
        <Route path="admin/ontology" element={<OntologyClientFallback target="versions" />} />
        <Route path="admin/ontology/:clientUid" element={<Navigate to="versions" replace />} />
        <Route path="admin/ontology/:clientUid/versions" element={<VersionBrowser />} />
        <Route path="admin/ontology/:clientUid/versions/diff" element={<VersionDiff />} />
        <Route path="admin/ontology/:clientUid/concepts" element={<ConceptInspector />} />
        <Route path="admin/ontology/:clientUid/concepts/:conceptId" element={<ConceptDetail />} />
        <Route path="admin/ontology/:clientUid/builds" element={<BuildInspector />} />
        <Route path="admin/ontology/:clientUid/explorer" element={<OntologyExplorer />} />
        <Route path="admin/ontology/:clientUid/explorer/:version" element={<OntologyExplorer />} />
        <Route path="admin/garden" element={<OntologyClientFallback target="garden" />} />
        <Route path="admin/garden/:clientUid" element={<GardenStudio />} />
        <Route path="admin/garden/:clientUid/compass" element={<CompassComparison />} />
        {/* Creative Review section — runs dashboard + prompt tuning */}
        <Route
          path="admin/creative-review"
          element={<CreativeReviewClientFallback />}
        />
        <Route
          path="admin/creative-review/:clientUid"
          element={<Navigate to="runs" replace />}
        />
        <Route
          path="admin/creative-review/:clientUid/runs/:runId"
          element={<RunDetailView />}
        />
        <Route
          path="admin/creative-review/:clientUid/graph-builds/:buildId"
          element={<GraphBuildDetailView />}
        />
        <Route
          path="admin/creative-review/:clientUid/runs"
          element={<CreativeReviewPage tab="runs" />}
        />
        <Route
          path="admin/creative-review/:clientUid/briefs"
          element={<CreativeReviewPage tab="briefs" />}
        />
        <Route
          path="admin/creative-review/:clientUid/tuning"
          element={<CreativeReviewPage tab="tuning" />}
        />

        {/* Knowledge Intelligence — Phase 0 */}
        <Route path="knowledge" element={<Navigate to="/knowledge/estate/entities" replace />} />
        <Route path="knowledge/:canonicalId" element={<KnowledgeDeepLinkRedirect />} />
        <Route path="knowledge/estate" element={<Navigate to="/knowledge/estate/entities" replace />} />
        <Route path="knowledge/estate/entities" element={<KnowledgeManagementPage />} />
        <Route path="knowledge/estate/entities/:canonicalId" element={<KnowledgeManagementPage />} />
        <Route path="knowledge/estate/concepts" element={<RawConceptsPage />} />
        <Route path="knowledge/estate/concepts/:conceptId" element={<RawConceptsPage />} />
        <Route path="knowledge/estate/runs" element={<ResearchRunsPage />} />
        <Route path="knowledge/estate/runs/:runId" element={<ResearchRunsPage />} />
        <Route path="knowledge/estate/lineage" element={<LineagePage />} />
        <Route path="knowledge/estate/lineage/:rootId" element={<LineagePage />} />
        {/* Creative Strategy: a Library is the category level, a Docket is one client's working set. */}
        <Route path="knowledge/creative-strategy" element={<Navigate to="/knowledge/creative-strategy/library" replace />} />
        <Route path="knowledge/creative-strategy/library" element={<CreativeStrategyIndexPage view="libraries" />} />
        <Route path="knowledge/creative-strategy/libraries" element={<Navigate to="/knowledge/creative-strategy/library" replace />} />
        <Route path="knowledge/creative-strategy/libraries/:docketId" element={<StrategyRedirect to="library" />} />
        <Route path="knowledge/creative-strategy/library/:docketId" element={<DocketPage />} />
        <Route path="knowledge/creative-strategy/dockets" element={<CreativeStrategyIndexPage view="dockets" />} />
        <Route path="knowledge/creative-strategy/dockets/:clientUid" element={<CreativeStrategyPackPage />} />
        <Route path="knowledge/creative-strategy/dockets/:clientUid/:itemId" element={<CreativeStrategyPackPage />} />
        <Route path="knowledge/creative-strategy/templates" element={<StrategyTemplatesPage />} />
        <Route path="knowledge/creative-strategy/corpus" element={<CorpusPage />} />
        <Route path="knowledge/creative-strategy/corpus/:docketId/:sourceId" element={<CorpusPage />} />
        {/* Earlier URLs: /docket/:id was the category level, /packs was the per-client view. */}
        <Route path="knowledge/creative-strategy/docket/:docketId" element={<StrategyRedirect to="library" />} />
        <Route path="knowledge/creative-strategy/packs" element={<Navigate to="/knowledge/creative-strategy/dockets" replace />} />
        <Route path="knowledge/creative-strategy/packs/:clientUid" element={<StrategyRedirect to="dockets" />} />
        <Route path="knowledge/creative-strategy/packs/:clientUid/:itemId" element={<StrategyRedirect to="dockets" />} />
        <Route path="knowledge/creative-strategy/:clientUid" element={<StrategyRedirect to="dockets" />} />
        <Route path="knowledge/creative-strategy/:clientUid/:itemId" element={<StrategyRedirect to="dockets" />} />
        {/* Catalogs (Phase 1) */}
        <Route path="knowledge/catalogs" element={<CatalogIndexPage />} />
        <Route path="knowledge/catalogs/ontology" element={<OntologySchemaPage />} />
        <Route path="knowledge/catalogs/concepts" element={<ConceptTypePage />} />
        <Route path="knowledge/catalogs/ad-taxonomy" element={<AdTaxonomyCatalogPage />} />
        <Route path="knowledge/catalogs/proposed" element={<ProposedCanonicalsPage />} />

        {/* Fleet (Phase 2) */}
        <Route path="knowledge/fleet" element={<FleetDashboardPage />} />
        <Route path="knowledge/fleet/nvi" element={<FleetDashboardPage defaultTab="nvi" />} />
        <Route path="knowledge/fleet/activity" element={<FleetDashboardPage defaultTab="activity" />} />
        <Route path="knowledge/fleet/schedules" element={<FleetDashboardPage defaultTab="schedules" />} />
        <Route path="knowledge/fleet/:clientId" element={<ClientFleetDetailPage />} />

        {/* Resolution Pipeline + Review Queue (Phase 3) */}
        <Route path="knowledge/resolution" element={<ResolutionPipelinePage />} />
        <Route path="knowledge/resolution/review" element={<ReviewQueuePage />} />

        {/* Quality Dashboard (Phase 4) */}
        <Route path="knowledge/quality" element={<QualityDashboardPage />} />

        <Route path="insights" element={<InsightsPage />} />
        {/* Old location — redirect to new catalog home (Phase 1 builds dedicated page). */}
        <Route path="taxonomy" element={<Navigate to="/knowledge/catalogs/ad-taxonomy" replace />} />
        <Route path="personas" element={<PersonasPage />} />
        <Route path="personas/:clientUid/:personaSlug" element={<PersonaDetailPage />} />
        <Route path="scenarios" element={<ScenariosPage />} />
        <Route path="scenarios/:scenarioId" element={<ScenariosPage />} />
        {/* Old location — redirect to new fleet home (Phase 2 swaps in FleetDashboardPage). */}
        <Route path="queue" element={<Navigate to="/knowledge/fleet" replace />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="mcp-connectors" element={<McpConnectorsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        {/* Skills Intelligence — agency growth & optimization */}
        <Route path="skills-catalog" element={<SkillsCatalogPage />} />
        <Route path="skill-library" element={<SkillLibraryPage />} />
        <Route path="client-playbooks" element={<ClientPlaybooksPage />} />
        {/* Phase C Workstream 3 — Playbook Explorer (Creative Memory) */}
        <Route path="playbooks" element={<PlaybookListPage />} />
        <Route path="playbooks/:playbookId" element={<PlaybookDetailPage />} />
        <Route path="usage-activity" element={<UsageActivityPage />} />
        {/* Phase 5 — Creative Memory admin section. Promoted to a
            first-class left-nav group; the legacy /playbooks routes
            stay live as aliases so old bookmarks keep working. */}
        <Route path="creative-memory" element={<CreativeMemoryOverviewPage />} />
        <Route path="creative-memory/playbooks" element={<PlaybookListPage />} />
        <Route
          path="creative-memory/playbooks/:playbookId"
          element={<PlaybookDetailPage />}
        />
        <Route path="creative-memory/routing" element={<PersonaRoutingPage />} />
        <Route
          path="creative-memory/decisions"
          element={<DecisionsBrowserPage />}
        />
        <Route
          path="creative-memory/escalations"
          element={<EscalationsPage />}
        />
        <Route path="performance-loop" element={<PerformanceLoopPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
